import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Contrast, LogOut, Settings, SlidersHorizontal } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Switch } from '@/shared/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { SidebarMenuButton } from '@/shared/ui/sidebar';

import { useCurrentUserQuery } from '@/entities/user';
import { logout } from '@/shared/auth';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettingsAccess } from '@/components/settings/access';
import { getInitials, makeInitialSrc, pickColor } from './avatarUtils';

export function UserMenuWidget() {
  const navigate = useNavigate();
  const { data: user } = useCurrentUserQuery();
  const { colorScheme, toggleTheme } = useTheme();
  const { canAccessAdmin, visibleAgents } = useSettingsAccess();

  const userEmail = user?.email ?? 'User';
  const userLabel = user?.name ?? userEmail;
  const avatarInitials = getInitials(user?.name, userEmail);
  const avatarColor = useMemo(() => pickColor(userEmail), [userEmail]);
  const avatarSrc = useMemo(() => makeInitialSrc(avatarInitials, avatarColor), [avatarInitials, avatarColor]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          aria-label="User menu"
          tooltip="User menu"
          className="h-9 text-left font-medium group-data-[collapsible=icon]:!size-7 group-data-[collapsible=icon]:justify-center"
        >
          <Avatar className="size-7">
            <AvatarImage src={user?.avatarUrl ?? avatarSrc} alt={userLabel} />
            <AvatarFallback>{avatarInitials}</AvatarFallback>
          </Avatar>
          <span className="min-w-0 flex-1 truncate text-xs font-medium group-data-[collapsible=icon]:hidden">
            {userLabel}
          </span>
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-60">
        {/*
          Section 1: Identity. The user name is shown as a non-interactive
          label so it never renders with the muted/disabled item color.
        */}
        <DropdownMenuLabel className="text-foreground">
          <div className="text-sm font-bold">{userLabel}</div>
          {user?.email ? <div className="truncate text-xs font-normal text-muted-foreground">{user.email}</div> : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Section 2: actions and external help resources. */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <SlidersHorizontal />
            Preferences
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-65">
            <DropdownMenuItem onSelect={toggleTheme} className="justify-between">
              <span className="flex min-w-0 items-center gap-2">
                <Contrast aria-hidden="true" />
                Theme
              </span>
              <Switch
                checked={colorScheme === 'dark'}
                onCheckedChange={() => {}}
                tabIndex={-1}
                aria-hidden="true"
                className="pointer-events-none"
              />
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {canAccessAdmin || visibleAgents.length > 0 ? (
          <DropdownMenuItem onSelect={() => navigate('/settings')}>
            <Settings />
            Settings
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => void logout()}>
          <LogOut />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
